import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { leaveApi } from "../api";
import { LeaveApplicationForm } from "../components/LeaveApplicationForm";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { useAsyncData, useMutation } from "../hooks";
import { clearDraft, formatDraftAge, loadDraft, saveDraft } from "../lib/draft";
import type { LeaveApplyRequest } from "../types";

export default function LeaveRequestCreatePage() {
  const navigate = useNavigate();

  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const eligibleTypes = useAsyncData(() => leaveApi.eligibleLeaveTypes(), []);
  const setup = useAsyncData(() => leaveApi.setupStatus(), []);

  const create = useMutation((payload: LeaveApplyRequest) =>
    leaveApi.apply(payload),
  );

  const employeeId = employee.data?.id;
  const draft = employeeId ? loadDraft(employeeId) : null;

  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(
    draft?.savedAt ?? null,
  );
  const [discarded, setDiscarded] = useState(false);

  function handleSaveDraft(payload: LeaveApplyRequest) {
    if (!employeeId) return;
    saveDraft(employeeId, payload);
    setDraftSavedAt(new Date().toISOString());
  }

  function handleDiscardDraft() {
    if (!employeeId) return;
    clearDraft(employeeId);
    setDiscarded(true);
    setDraftSavedAt(null);
  }

  async function handleSubmit(payload: LeaveApplyRequest) {
    const response = await create.mutate(payload);
    if (employeeId) clearDraft(employeeId);
    navigate(`/hr/leave/requests/${response.id}`);
  }

  function friendlyError(msg: string | null): string | null {
    if (!msg) return null;
    if (msg.includes("NO_ELIGIBLE_APPROVER") || msg.includes("No available approver")) {
      return "No approver could be found for one of the workflow steps. This usually means your department has no Head of Department assigned, or the workflow step requires a specific role/user that isn't configured. Please contact HR or your administrator to check the leave workflow setup.";
    }
    if (msg.includes("WORKFLOW_NOT_CONFIGURED") || msg.includes("No active workflow")) {
      return "The leave approval workflow has not been configured yet. Please contact your administrator to set up the Leave workflow before applying.";
    }
    if (
      msg.includes("RELIEF_EMPLOYEE_ON_LEAVE") ||
      msg.toLowerCase().includes("relief") && msg.toLowerCase().includes("on leave")
    ) {
      return "The selected relief employee is on leave during your requested dates. Please go back and choose a different relief employee who is available.";
    }
    return msg;
  }

  const loading = employee.loading || eligibleTypes.loading || setup.loading;

  const activeDraft = !discarded && draft ? draft : null;

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Apply for Leave"
        actions={
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
          >
            Back
          </button>
        }
      />

      {loading ? <LeaveLoading label="Preparing leave form..." /> : null}

      {employee.error ? <LeaveError message={employee.error} /> : null}
      {eligibleTypes.error ? <LeaveError message={eligibleTypes.error} /> : null}
      {setup.error ? <LeaveError message={setup.error} /> : null}
      {create.error ? <LeaveError message={friendlyError(create.error) ?? create.error} /> : null}

      {setup.data && !setup.data.complete ? (
        <LeaveError message={setup.data.message} />
      ) : null}

      {draftSavedAt && (
        <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <span className="font-semibold text-amber-800">
            Draft saved — {formatDraftAge(draftSavedAt)}. Your progress is preserved and will be restored when you return.
          </span>
          <button
            type="button"
            onClick={handleDiscardDraft}
            className="ml-4 shrink-0 text-xs font-bold text-amber-600 underline hover:text-amber-800"
          >
            Discard draft
          </button>
        </div>
      )}

      {employee.data && eligibleTypes.data && setup.data?.complete ? (
        eligibleTypes.data.length ? (
          <LeaveApplicationForm
            employee={employee.data}
            leaveTypes={eligibleTypes.data}
            submitting={create.loading}
            initialValues={activeDraft ?? undefined}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
          />
        ) : (
          <LeaveError message="No eligible leave type was found for your profile. Please contact HR." />
        )
      ) : null}
    </div>
  );
}
